// src/App.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import './styles/global.css';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import Home from './components/Home/Home';
import ProjectsPage from './components/Projects/ProjectsPage';
import EditorLayout from './components/Editor/EditorLayout';
import DocLayout from './components/Editor/DocLayout';
import Icon from './components/Icon';
import RequireAuth from './components/Auth/RequireAuth';
import RequireAdmin from './components/Admin/RequireAdmin';
import { setDebugUser, debug } from './helpers/debug';
import { getPreference } from './helpers/preferencesHelpers';
import { syncAuthToUser } from './api/users';
import { supabase } from './supabaseClient';
import { registerNotificationChannel, unregisterNotificationChannel } from './helpers/notificationHelpers';
import { buildToastStackKey, removeToastEntry, updateToastEntry, upsertToastEntry } from './helpers/toastStackHelpers';

const Auth = React.lazy(() => import('./components/Auth'));
const PasswordReset = React.lazy(() => import('./components/Auth/PasswordReset'));
const PublicDocLayout = React.lazy(() => import('./components/Editor/PublicDocLayout'));
const PublicProjectLayout = React.lazy(() => import('./components/Editor/PublicProjectLayout'));
const AdminRoutes = React.lazy(() => import('./components/Admin/AdminRoutes'));

export default function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toastStacks, setToastStacks] = useState([]);
  const navigate = useNavigate();
  const hasHandledInitialAuth = useRef(false);
  const toastTimeoutsRef = useRef(new Map());

  const applyThemeMode = useCallback((mode) => {
    if (typeof window === 'undefined' || typeof document === 'undefined' || !document.body) {
      return () => {};
    }

    const normalized = mode === 'night' || mode === 'system' ? mode : 'light';
    const applyNight = (isNight) => {
      if (isNight) document.body.setAttribute('data-theme', 'night');
      else document.body.removeAttribute('data-theme');
    };

    let mql = null;
    let mqlHandler = null;

    if (normalized === 'night') {
      applyNight(true);
    } else if (normalized === 'system') {
      mql = window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null;
      applyNight(!!mql?.matches);
      mqlHandler = (event) => applyNight(!!event?.matches);
      if (mql && typeof mql.addEventListener === 'function') {
        mql.addEventListener('change', mqlHandler);
      } else if (mql && typeof mql.addListener === 'function') {
        mql.addListener(mqlHandler);
      }
    } else {
      applyNight(false);
    }

    return () => {
      if (mql && mqlHandler && typeof mql.removeEventListener === 'function') {
        mql.removeEventListener('change', mqlHandler);
      } else if (mql && mqlHandler && typeof mql.removeListener === 'function') {
        mql.removeListener(mqlHandler);
      }
    };
  }, []);

  const getToastContainer = useCallback((where = null) => {
    if (typeof document === 'undefined' || typeof window === 'undefined') return null;

    const centerContainer =
      document.getElementById('editorContainer') ||
      document.getElementById('main') ||
      document.getElementById('app') ||
      document.body ||
      null;

    const docId = where?.docId;
    if (docId !== undefined && docId !== null) {
      const docElement = document.getElementById(`ghost-${docId}`);
      if (docElement) {
        let isVisible = false;
        try {
          const cs = window.getComputedStyle(docElement);
          isVisible = cs.display !== 'none' && cs.visibility !== 'hidden' && cs.opacity !== '0' && Boolean(docElement.offsetParent || docElement.getClientRects?.().length);
        } catch (err) {
          isVisible = Boolean(docElement.offsetParent || docElement.getClientRects?.().length);
        }

        if (isVisible) {
          // Mount into the non-scrolling editor panel so the toast does not
          // move with the document scroll inside editorPanelScroll.
          return docElement.closest('.editorPanel') || docElement;
        }
      }
    }

    const pane = String(where?.pane || '').toLowerCase();
    if (pane === 'left') {
      return document.getElementById('editorPanelLeft') || centerContainer;
    }
    if (pane === 'right') {
      return document.getElementById('editorPanelRight') || centerContainer;
    }

    return centerContainer;
  }, []);

  const clearToastTimer = useCallback((stackKey, toastId) => {
    const timerKey = `${String(stackKey || '')}:${String(toastId || '')}`;
    const active = toastTimeoutsRef.current.get(timerKey);
    if (active) {
      clearTimeout(active);
      toastTimeoutsRef.current.delete(timerKey);
    }
  }, []);

  const dismissToastById = useCallback((toastId, stackKey = null) => {
    if (!toastId) return;

    if (stackKey) {
      clearToastTimer(stackKey, toastId);
      setToastStacks((prev) => {
        return prev
          .map((stack) => {
            if (stack.key !== stackKey) return stack;
            const nextEntries = removeToastEntry(stack.entries, toastId);
            return nextEntries.length > 0 ? { ...stack, entries: nextEntries } : null;
          })
          .filter(Boolean);
      });
      return;
    }

    setToastStacks((prev) => prev
      .map((stack) => {
        clearToastTimer(stack.key, toastId);
        const nextEntries = removeToastEntry(stack.entries, toastId);
        return nextEntries.length > 0 ? { ...stack, entries: nextEntries } : null;
      })
      .filter(Boolean));
  }, [clearToastTimer]);

  const hideToastById = useCallback((toastId, stackKey) => {
    if (!toastId || !stackKey) return;
    clearToastTimer(stackKey, toastId);
    setToastStacks((prev) => prev.map((stack) => {
      if (stack.key !== stackKey) return stack;
      return {
        ...stack,
        entries: updateToastEntry(stack.entries, toastId, { visible: false })
      };
    }));

    const removalTimer = setTimeout(() => {
      dismissToastById(toastId, stackKey);
    }, 220);
    toastTimeoutsRef.current.set(`${String(stackKey)}:${String(toastId)}`, removalTimer);
  }, [clearToastTimer, dismissToastById]);

  const scheduleToastDismiss = useCallback((toastId, stackKey, timeoutMs) => {
    if (!toastId || !stackKey || !(timeoutMs > 0)) return;
    clearToastTimer(stackKey, toastId);
    const timeoutHandle = setTimeout(() => {
      hideToastById(toastId, stackKey);
    }, timeoutMs);
    toastTimeoutsRef.current.set(`${String(stackKey)}:${String(toastId)}`, timeoutHandle);
  }, [clearToastTimer, hideToastById]);

  // Log build info on mount
  useEffect(() => {
    debug('App', '🚀 Ghost Build Info:', {
      buildTime: process.env.REACT_APP_BUILD_TIME || 'dev',
      commitHash: process.env.REACT_APP_COMMIT_HASH || 'local',
      nodeEnv: process.env.NODE_ENV
    });
  }, []);

  const handleAuthReady = useCallback(async (authUser, isInitialLoad = false) => {
    try {
      const fullUser = await syncAuthToUser(authUser);
      setUser(fullUser);
      setDebugUser(fullUser);
      setLoading(false);

      if (isInitialLoad) {
        debug('Auth', '✓ User authenticated:', {
          email: fullUser.email,
          id: fullUser.id,
          projects: fullUser.projects?.length || 0
        });
      }
    } catch (err) {
      setLoading(false);

      if (isInitialLoad) {
        debug('Auth', '✗ Failed to sync user:', { type: 'error' }, err);
      }
    }
  }, []);

  const navigateRef = useRef(navigate);
  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user) {
        if (event === 'INITIAL_SESSION' || event === 'SIGNED_IN') {
          if (!hasHandledInitialAuth.current) {
            hasHandledInitialAuth.current = true;
            handleAuthReady(session.user, true);
          }
        } else {
          handleAuthReady(session.user, false);
        }
      } else {
        setUser(null);
        setLoading(false);
        if (event === 'SIGNED_OUT') {
          hasHandledInitialAuth.current = false;
          navigateRef.current('/auth', { replace: true });
        }
      }
    });

    return () => {
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    try {
      const globalTheme = getPreference(null, 'theme', '');
      const mode = globalTheme === 'night' || globalTheme === 'system' ? globalTheme : 'light';
      return applyThemeMode(mode);
    } catch (e) {
      // ignore
    }
    return () => {};
  }, [applyThemeMode]);

  useEffect(() => {
    try {
      const stored = getPreference(user?.id ?? null, 'theme', null);
      if (stored === null || stored === '') return () => {};
      const mode = stored === 'night' || stored === 'system' ? stored : 'light';
      return applyThemeMode(mode);
    } catch (e) {
      // ignore
    }
    return () => {};
  }, [applyThemeMode, user]);

  useEffect(() => {
    const activeTimeouts = toastTimeoutsRef.current;

    registerNotificationChannel('toast', (payload = {}) => {
      const dismiss = Boolean(payload?.dismiss);
      const payloadToastId = payload?.toastId ?? null;
      const where = payload?.where || null;
      const resolvedTarget = getToastContainer(where);
      const stackKey = buildToastStackKey(where, 'app');

      if (dismiss) {
        dismissToastById(payloadToastId, stackKey);
        return;
      }

      const rawMessage = payload?.message;
      const message = typeof rawMessage === 'string' ? rawMessage.trim() : rawMessage;
      const suggestedName = typeof payload?.suggestedName === 'string' ? payload.suggestedName.trim() : '';
      const isPrompt = Boolean(payload?.isPrompt);
      if (message == null || message === '') return;
      if (isPrompt && !suggestedName) return;
      const timeoutMsRaw = Number(payload?.timeoutMs);
      const timeoutMs = Number.isFinite(timeoutMsRaw) ? timeoutMsRaw : 4200;

      const entryToastId = String(payloadToastId || `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`);
      const nextEntry = {
        id: `${entryToastId}-${Date.now()}`,
        toastId: entryToastId,
        message,
        suggestedName,
        isPrompt,
        level: payload?.level || 'info',
        visible: true,
        where,
        persistent: Boolean(payload?.persistent) || timeoutMs <= 0,
        actions: Array.isArray(payload?.actions) ? payload.actions : [],
        createdAt: Date.now()
      };

      const entryWithContent = {
        ...nextEntry,
        messageContent: typeof message === 'string' && nextEntry?.suggestedName
          ? <>
              <span>{`${message} `}</span>
              <strong>{nextEntry.suggestedName}</strong>
              {nextEntry?.isPrompt ? <span>?</span> : null}
            </>
          : message
      };

      setToastStacks((prev) => {
        const existingIndex = prev.findIndex((stack) => stack.key === stackKey);
        if (existingIndex < 0) {
          return [...prev, { key: stackKey, target: resolvedTarget, entries: upsertToastEntry([], entryWithContent) }];
        }

        const nextStacks = [...prev];
        const existing = nextStacks[existingIndex];
        nextStacks[existingIndex] = {
          ...existing,
          target: resolvedTarget,
          entries: upsertToastEntry(existing.entries, entryWithContent)
        };
        return nextStacks;
      });

      if (!(Boolean(payload?.persistent) || timeoutMs <= 0)) {
        scheduleToastDismiss(entryToastId, stackKey, timeoutMs);
      }
    });

    return () => {
      activeTimeouts.forEach((handle) => clearTimeout(handle));
      activeTimeouts.clear();
      unregisterNotificationChannel('toast');
    };
  }, [dismissToastById, getToastContainer, scheduleToastDismiss]);

  const appContent = loading ? (
    <div />
  ) : (
    <Routes>
      <Route
        path="/"
        element={<Home user={user} />}
      />

      <Route
        path="/auth/:mode?"
        element={
          user ? (
            <Navigate to="/projects" replace />
          ) : (
            <React.Suspense fallback={<div />}>
              <Auth />
            </React.Suspense>
          )
        }
      />

      <Route
        path="/auth/reset"
        element={
          user ? (
            <Navigate to="/projects" replace />
          ) : (
            <React.Suspense fallback={<div />}>
              <PasswordReset />
            </React.Suspense>
          )
        }
      />

      <Route
        path="/projects"
        element={
          <RequireAuth user={user}>
            <ProjectsPage user={user} />
          </RequireAuth>
        }
      />

      <Route
        path="/admin/*"
        element={
          <RequireAdmin user={user}>
            <React.Suspense fallback={<div />}>
              <AdminRoutes user={user} />
            </React.Suspense>
          </RequireAdmin>
        }
      />

      <Route
        path="/p/:projectId"
        element={
          <RequireAuth user={user}>
            <EditorLayout user={user} />
          </RequireAuth>
        }
      />

      <Route
        path="/d/:docId"
        element={
          <RequireAuth user={user}>
            <DocLayout user={user} />
          </RequireAuth>
        }
      />

      {/* Public share routes — no auth required */}
      <Route
        path="/share/d/:token"
        element={
          <React.Suspense fallback={<div />}>
            <PublicDocLayout />
          </React.Suspense>
        }
      />

      <Route
        path="/share/p/:token"
        element={
          <React.Suspense fallback={<div />}>
            <PublicProjectLayout />
          </React.Suspense>
        }
      />

      <Route
        path="*"
        element={<Navigate to={user ? '/projects' : '/'} replace />}
      />
    </Routes>
  );

  const toastHosts = toastStacks
    .filter((stack) => stack?.target && stack.target.isConnected !== false && Array.isArray(stack.entries) && stack.entries.length > 0)
    .map((stack) => {
      const toastHost = (
        <div
          id={`notificationOrigin-${stack.key}`}
          className="notification-origin"
          aria-live="polite"
          aria-atomic="true"
        >
          {stack.entries.map((entry) => (
            <div
              key={entry.id}
              className={`notificationToast ${entry.visible ? 'show' : ''} ${entry.level || 'info'} ${entry.persistent ? 'persistent' : ''}`.trim()}
              role="status"
            >
              <div className="notificationToastBody">
                <div className="notificationToastMessage">{entry.messageContent ?? entry.message}</div>
                {Array.isArray(entry.actions) && entry.actions.length > 0 ? (
                  <div className="notificationToastActions">
                    {entry.actions.map((action, index) => {
                      const actionKey = action?.id || `${entry.toastId}-action-${index}`;
                      const iconClass = String(action?.iconClass || '').trim();
                      const iconName = iconClass ? iconClass.split(/\s+/)[0] : '';
                      const label = String(action?.label || '').trim();
                      const dismissOnClick = action?.dismissOnClick !== false;
                      const commonProps = {
                        key: actionKey,
                        className: 'iconButton small notificationToastAction',
                        title: label || undefined,
                        'aria-label': label || 'Toast action',
                        onClick: (event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          action?.onClick?.();
                          if (dismissOnClick) {
                            dismissToastById(entry.toastId, stack.key);
                          }
                        }
                      };

                      if (action?.href) {
                        return (
                          <a
                            {...commonProps}
                            href={action.href}
                            target={action.target || '_blank'}
                            rel={action.rel || 'noreferrer'}
                          >
                            {iconName ? (
                              <Icon icon={iconName} className={`icon small ${iconClass}`.trim()} />
                            ) : (label || 'Open')}
                          </a>
                        );
                      }

                      return (
                        <button
                          {...commonProps}
                          type="button"
                        >
                          {iconName ? (
                            <Icon icon={iconName} className={`icon small ${iconClass}`.trim()} />
                          ) : (label || null)}
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      );

      return createPortal(toastHost, stack.target);
    });

  return (
    <>
      {appContent}
      {toastHosts}
    </>
  );
}
